import type { IDesignEntity, ILocalizedText, IModuleDefinition, IRecordTypeDefinition } from '@platform/core/types';
import { isTableEntity } from './designEntityRules';
import type { IConfigTable } from './IConfigTable';
import { isDesigned, isHiddenType, kindOf, mainRecordTypeOf } from './moduleSections';

const en = (text: ILocalizedText | null | undefined): string => (text?.en ?? '').trim().toLowerCase();

/**
 * Пункти групи «Таблиці» в налаштуваннях і «Моделі даних» модуля без власного меню (прототип v4 rtGroups: [головна, ...дочірні модулі в
 * порядку ws.modules, ...сутності]): головна, реалізовані дочірні за порядком простору (`orderOf` — каталог: order; без нього — порядок
 * визначення, стабільно), далі сутності прототипу головного типу (`designEntities`, isTableEntity; підпис — `label`) з типом, підсвіченим на
 * «Записах і формах». Без `designEntities` (визначення до їх появи) — спроєктовані дочірні самої головної таблиці (`parent` — головний тип),
 * крім повторів реалізованої («Draft Article» — «Article»); без довідників і прихованих типів. `label` — підпис сутності мовою інтерфейсу
 * (designEntityLabel). Чиста функція.
 */
export function configTablesOf(
  definition: IModuleDefinition, label: (rtKey: string, index: number, entity: IDesignEntity) => string, orderOf?: (rtKey: string) => number,
): IConfigTable[] {
  const main = mainRecordTypeOf(definition);
  if (!main) return [];
  const children = definition.recordTypes.filter((rt) => rt.key !== main.key && kindOf(rt) === 'child' && !isHiddenType(rt));
  const built = children.filter((rt) => !isDesigned(rt));
  if (orderOf) built.sort((a, b) => orderOf(a.key) - orderOf(b.key));
  const tables: IConfigTable[] = [main, ...built].map((rt) => ({ key: rt.key, rt }));
  if (main.designEntities) {
    return [...tables, ...main.designEntities.flatMap((e, i): IConfigTable[] => (isTableEntity(e, main, built)
      ? [{ key: `entity:${i}`, label: label(main.key, i, e), focus: e.recordType ?? e.name.en ?? '' }] : []))];
  }
  const repeats = (rt: IRecordTypeDefinition): boolean => {
    const one = en(rt.singular);
    return Boolean(one) && built.some((k) => one === en(k.singular) || one === en(k.name) || one.endsWith(` ${en(k.singular)}`));
  };
  const ofMain = (rt: IRecordTypeDefinition): boolean => !rt.parent || rt.parent === main.key;
  return [...tables, ...children.filter((rt) => isDesigned(rt) && ofMain(rt) && !repeats(rt)).map((rt) => ({ key: rt.key, rt }))];
}
